import { Form, Formik } from 'formik';
import { TFunction } from 'i18next';
import { useUnsubscribeMutation } from 'lib/api/unsubscribe/useUnsubscribeMutation';
import { FormikInputField } from 'lib/components/formik/FormikInputField';
import { Flex, MainWrapper } from 'lib/components/styles/layout';
import {
  Heading,
  ParagraphNormal,
  ParagraphNormal14,
} from 'lib/components/styles/typography';
import queryString from 'query-string';
import React, { useState, useMemo } from 'react';
import { Button } from 'react-covideo-common';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import styled from 'styled-components/macro';
import * as Yup from 'yup';

interface UnsubscribeFormValues {
  email: string;
}
const ContentWrapper = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const getValidationSchema = (t: TFunction) =>
  Yup.object().shape({
    email: Yup.string()
      .email(t('Invalid email address'))
      .required(t('Email is required')),
  });

export const UnsubscribeContent = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const queryParams = queryString.parse(location.search);
  const user = queryParams?.user?.toString();
  const sender = queryParams?.sender?.toString();
  const emailParam = queryParams?.email?.toString();
  const [success, setSuccess] = useState(false);
  const validationSchema = useMemo(() => getValidationSchema(t), [t]);

  // Decode email from base64
  const email = !!emailParam?.trim()?.length ? atob(emailParam) : '';
  const decodedSender = !!sender?.trim()?.length ? atob(sender) : '';
  const initialValues: UnsubscribeFormValues = { email };

  const { mutateAsync: unsubscribeMutation } = useUnsubscribeMutation();

  const handleSubmit = async (values: UnsubscribeFormValues) => {
    if (!user || !sender) {
      return;
    }
    await unsubscribeMutation({
      user,
      sender,
      email: values.email,
    });
    setSuccess(true);
  };

  const handleCancel = () => {
    if (window.opener) {
      window.close();
      return;
    }

    window.history.back();
  };

  if (!user || !sender) {
    return (
      <MainWrapper resetPadding={false}>
        <ContentWrapper>
          <Flex justifyContent='center' alignItems='center' gap='16px'>
            <Heading>{t('Error')}</Heading>
            <ParagraphNormal>
              {t('Invalid unsubscribe link. Required parameters are missing.')}
            </ParagraphNormal>
          </Flex>
        </ContentWrapper>
      </MainWrapper>
    );
  }

  if (success) {
    return (
      <MainWrapper resetPadding={false}>
        <ContentWrapper>
          <Flex justifyContent='center' alignItems='center' gap='16px'>
            <Heading>{t('Unsubscribe Confirmation')}</Heading>
            <ParagraphNormal>
              {t('You have successfully unsubscribed.')}
            </ParagraphNormal>
          </Flex>
        </ContentWrapper>
      </MainWrapper>
    );
  }

  return (
    <MainWrapper resetPadding={false}>
      <ContentWrapper>
        <Formik<UnsubscribeFormValues>
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form>
              <Flex justifyContent='center' alignItems='center' gap='16px'>
                <Heading>{t('Please confirm your unsubscribe')}</Heading>
                {email ? (
                  <ParagraphNormal14>
                    {t('Your email address:')} <strong>{email}</strong>
                  </ParagraphNormal14>
                ) : (
                  <FormikInputField
                    name='email'
                    label={t('Email Address')}
                    isRequired={true}
                  />
                )}

                <ParagraphNormal style={{ textAlign: 'center' }}>
                  {t(
                    'Are you sure you wish to stop all video emails and broadcast invitation from'
                  )}{' '}
                  <strong>{decodedSender}</strong>{' '}
                  {t('sent to your email address?')}
                </ParagraphNormal>

                <Flex
                  justifyContent='center'
                  alignItems='center'
                  flexDirection='row'
                  gap='16px'
                >
                  <Button
                    text={
                      isSubmitting ? t('Unsubscribing...') : t('Unsubscribe')
                    }
                    type='submit'
                    disabled={isSubmitting}
                    variant='primary'
                  />
                  <Button
                    text={t('Cancel')}
                    onClick={handleCancel}
                    variant='secondary'
                  />
                </Flex>
              </Flex>
            </Form>
          )}
        </Formik>
      </ContentWrapper>
    </MainWrapper>
  );
};
